import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 6  -  Particle Detectors 101
// ============================================

/** 6.2 – Tracking Detectors */
export const TrackingDetectorDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Tracking Detectors  -  How Charged Particles Leave Trails', W / 2, 24);

        // Detector layers (concentric arcs)
        const cx = 180, cy = 160;
        const layers = [
            { r: 30, label: 'Pixel', color: '#3B82F6' },
            { r: 55, label: 'Strip', color: '#22C55E' },
            { r: 80, label: 'Strip', color: '#22C55E' },
            { r: 110, label: 'TPC/Drift', color: '#F59E0B' },
        ];
        layers.forEach(l => {
            ctx.strokeStyle = l.color; ctx.lineWidth = 4;
            ctx.beginPath(); ctx.arc(cx, cy, l.r, -Math.PI * 0.7, Math.PI * 0.7); ctx.stroke();
        });

        // IP dot
        ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('IP', cx + 8, cy + 4);

        // Curved tracks (charged particles in magnetic field)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let t = 0; t <= 1; t += 0.01) {
            const angle = -0.3 + t * 1.2;
            const r = 15 + t * 110;
            const x = cx + r * Math.cos(angle - 0.1);
            const y = cy + r * Math.sin(angle - 0.1);
            t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let t = 0; t <= 1; t += 0.01) {
            const angle = 0.2 + t * 0.8;
            const r = 15 + t * 120;
            const x = cx + r * Math.cos(angle + 0.3);
            const y = cy + r * Math.sin(angle + 0.2);
            t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Hits on layers (dots)
        ctx.fillStyle = '#1E293B';
        [[cx + 26, cy + 6], [cx + 48, cy + 18], [cx + 72, cy + 28], [cx + 100, cy + 42]].forEach(([x, y]) => {
            ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
        });

        // Legend (right side)
        const lx = 340, ly = 55;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Tracking Principle:', lx, ly);

        const steps = [
            { text: 'Charged particle ionises gas/silicon', icon: '1' },
            { text: 'Each layer records a "hit" position', icon: '2' },
            { text: 'Hits are connected to form a track', icon: '3' },
            { text: 'Curvature in B-field → momentum', icon: '4' },
        ];
        steps.forEach((s, i) => {
            const y = ly + 20 + i * 30;
            ctx.beginPath(); ctx.arc(lx + 10, y, 10, 0, Math.PI * 2);
            ctx.fillStyle = '#3B82F6'; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.icon, lx + 10, y + 4);
            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(s.text, lx + 26, y + 4);
        });

        // Layer labels
        ctx.fillStyle = '#3B82F6'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Pixel (10 μm)', lx, ly + 150);
        ctx.fillStyle = '#22C55E'; ctx.fillText('Silicon Strip (50 μm)', lx, ly + 168);
        ctx.fillStyle = '#F59E0B'; ctx.fillText('TPC / Drift Chamber', lx, ly + 186);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Trackers reconstruct particle paths with micrometer precision', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 6.3 – Calorimeters */
export const CalorimeterLayers = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Calorimeters  -  Measuring Particle Energy', W / 2, 24);

        const startY = 50, layerH = 90;

        // ECAL
        ctx.fillStyle = '#DBEAFE'; ctx.beginPath(); ctx.roundRect(40, startY, 480, layerH, 8); ctx.fill();
        ctx.strokeStyle = '#93C5FD'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(40, startY, 480, layerH, 8); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('ECAL (Electromagnetic Calorimeter)', 55, startY + 22);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Dense crystals (PbWO₄, CsI)', 55, startY + 42);
        ctx.fillText('Absorbs: electrons, photons (e/γ)', 55, startY + 58);
        ctx.fillText('Mechanism: EM showers', 55, startY + 74);

        // EM shower sketch inside ECAL
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5;
        const sx = 420, sy = startY + 45;
        ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx + 20, sy - 15); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx + 20, sy + 15); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx + 20, sy - 15); ctx.lineTo(sx + 40, sy - 25); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx + 20, sy - 15); ctx.lineTo(sx + 40, sy - 5); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx + 20, sy + 15); ctx.lineTo(sx + 40, sy + 5); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx + 20, sy + 15); ctx.lineTo(sx + 40, sy + 25); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('e/γ shower', sx + 20, sy + 40);

        // HCAL
        const hy = startY + layerH + 12;
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(40, hy, 480, layerH, 8); ctx.fill();
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(40, hy, 480, layerH, 8); ctx.stroke();
        ctx.fillStyle = '#D97706'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('HCAL (Hadronic Calorimeter)', 55, hy + 22);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Iron/brass + scintillator layers', 55, hy + 42);
        ctx.fillText('Absorbs: pions, protons, neutrons (hadrons)', 55, hy + 58);
        ctx.fillText('Mechanism: Hadronic showers', 55, hy + 74);

        // Hadronic shower
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 1.5;
        const hsx = 420, hsy = hy + 45;
        ctx.beginPath(); ctx.moveTo(hsx, hsy); ctx.lineTo(hsx + 15, hsy - 12); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx, hsy); ctx.lineTo(hsx + 18, hsy + 8); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx, hsy); ctx.lineTo(hsx + 12, hsy + 18); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx + 15, hsy - 12); ctx.lineTo(hsx + 30, hsy - 20); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx + 15, hsy - 12); ctx.lineTo(hsx + 32, hsy - 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx + 18, hsy + 8); ctx.lineTo(hsx + 35, hsy + 3); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(hsx + 18, hsy + 8); ctx.lineTo(hsx + 32, hsy + 20); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('hadronic shower', hsx + 20, hsy + 35);

        // Incoming particle arrow
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(15, startY + layerH + 6); ctx.lineTo(40, startY + layerH + 6); ctx.stroke();
        ctx.fillStyle = '#EF4444';
        ctx.beginPath(); ctx.moveTo(40, startY + layerH + 6); ctx.lineTo(34, startY + layerH + 2); ctx.lineTo(34, startY + layerH + 10); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('particle', 28, startY + layerH - 4);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Total energy deposit in ECAL + HCAL measures the particle energy', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 6.4 – Scintillation Counters & Time-of-Flight */
export const ScintillatorTOF = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Scintillation Counter & Time-of-Flight', W / 2, 24);

        const py = 120;
        // Scintillator bar
        ctx.fillStyle = '#A5F3FC'; ctx.beginPath(); ctx.roundRect(80, py - 25, 200, 50, 6); ctx.fill();
        ctx.strokeStyle = '#22D3EE'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(80, py - 25, 200, 50, 6); ctx.stroke();
        ctx.fillStyle = '#0891B2'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Scintillator', 180, py + 5);

        // Incoming particle
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(20, py); ctx.lineTo(80, py); ctx.stroke();
        ctx.fillStyle = '#EF4444';
        ctx.beginPath(); ctx.moveTo(80, py); ctx.lineTo(74, py - 4); ctx.lineTo(74, py + 4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('particle', 50, py - 12);

        // Photon flash inside
        ctx.fillStyle = '#FCD34D'; ctx.font = '16px Inter,sans-serif';
        ctx.fillText('✦', 150, py + 2); ctx.fillText('✦', 200, py - 6); ctx.fillText('✦', 170, py + 12);

        // Light guide to PMT
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(280, py - 15, 60, 30);
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1; ctx.strokeRect(280, py - 15, 60, 30);
        ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('Light Guide', 310, py + 36);

        // PMT
        ctx.fillStyle = '#C4B5FD'; ctx.beginPath(); ctx.roundRect(340, py - 20, 70, 40, 6); ctx.fill();
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(340, py - 20, 70, 40, 6); ctx.stroke();
        ctx.fillStyle = '#7C3AED'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('PMT', 375, py + 5);
        ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('Photomultiplier', 375, py + 36);

        // Signal arrow
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(410, py); ctx.lineTo(470, py); ctx.stroke();
        ctx.fillStyle = '#8B5CF6';
        ctx.beginPath(); ctx.moveTo(470, py); ctx.lineTo(464, py - 4); ctx.lineTo(464, py + 4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#8B5CF6'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Signal', 440, py - 12);

        // TOF section bottom
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('Time-of-Flight (TOF)', W / 2, py + 70);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Measure time between two scintillators → velocity', W / 2, py + 90);
        ctx.fillText('Combined with p from tracker → mass → particle ID!', W / 2, py + 108);

        // Formula
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('m² = p²(t²/L² - 1/c²)', W / 2, py + 135);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Scintillators produce light when charged particles pass through', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-cyan-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 6.5 – Bubble Chamber (case study) */
export const BubbleChamberSketch = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Bubble Chamber  -  Subatomic Photography', W / 2, 24);

        // Chamber body
        ctx.fillStyle = '#E0F2FE'; ctx.beginPath(); ctx.arc(200, 165, 110, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#60A5FA'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(200, 165, 110, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Superheated liquid', 200, 250);
        ctx.fillText('(liquid hydrogen)', 200, 264);

        // Bubble trails (spiral/curved tracks)
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1.5;
        // Track 1 - gentle curve
        ctx.beginPath();
        for (let t = 0; t <= 1; t += 0.01) {
            const x = 130 + t * 120;
            const y = 100 + t * 30 + Math.sin(t * 6) * 5;
            t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Track 2 - tighter curve (heavier particle)
        ctx.beginPath();
        for (let t = 0; t <= 1; t += 0.01) {
            const angle = -0.5 + t * 2.5;
            const r = 20 + t * 60;
            const x = 180 + r * Math.cos(angle);
            const y = 170 + r * Math.sin(angle) * 0.6;
            t === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Decay vertex - V shape
        ctx.beginPath(); ctx.moveTo(200, 160); ctx.lineTo(230, 130); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(200, 160); ctx.lineTo(240, 180); ctx.stroke();
        ctx.beginPath(); ctx.arc(200, 160, 3, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();

        // Bubble dots along tracks
        ctx.fillStyle = '#94A3B8';
        for (let i = 0; i < 15; i++) {
            const x = 135 + i * 9; const y = 102 + (i / 15) * 28 + Math.sin(i * 0.7) * 4;
            ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
        }

        // Right side: how it works
        const rx = 340, ry = 55;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('How It Works:', rx, ry);

        const steps = [
            'Liquid is superheated',
            'Particle ionises the liquid',
            'Bubbles form along the path',
            'Camera photographs bubbles',
            'Curvature reveals momentum',
        ];
        steps.forEach((s, i) => {
            const y = ry + 18 + i * 24;
            ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(`${i + 1}. ${s}`, rx, y);
        });

        ctx.fillStyle = '#475569'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('Invented: 1952', rx, ry + 160);
        ctx.fillText('Nobel Prize: 1960', rx, ry + 178);
        ctx.fillText('(Donald Glaser)', rx, ry + 196);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Bubble chambers produced millions of iconic particle physics images', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-sky-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 6.6 – Silicon Pixel Detectors & Triggers */
export const SiliconPixelDetector = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Modern Silicon Pixel Detectors & Trigger System', W / 2, 24);

        // Pixel grid (left side)
        const gx = 40, gy = 55, cellSize = 12, cols = 14, rows = 10;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Pixel Sensor Grid', gx, gy - 6);

        for (let r = 0; r < rows; r++) {
            for (let cc = 0; cc < cols; cc++) {
                const x = gx + cc * (cellSize + 2), y = gy + r * (cellSize + 2);
                ctx.fillStyle = '#F1F5F9'; ctx.fillRect(x, y, cellSize, cellSize);
                ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 0.5; ctx.strokeRect(x, y, cellSize, cellSize);
            }
        }

        // Hit pixels (particle track)
        const hitPixels = [[3, 8], [4, 7], [5, 6], [5, 5], [6, 4], [7, 3], [8, 2], [9, 1]];
        hitPixels.forEach(([r, cc]) => {
            ctx.fillStyle = '#EF4444';
            ctx.fillRect(gx + cc * (cellSize + 2), gy + r * (cellSize + 2), cellSize, cellSize);
        });

        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('← Hit pixels (charge deposited)', gx + cols * (cellSize + 2) + 8, gy + 5 * (cellSize + 2) + 6);

        // Pixel specs
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Pixel size: 50 �- 50 μm²', gx, gy + rows * (cellSize + 2) + 20);
        ctx.fillText('Resolution: ~10 μm', gx, gy + rows * (cellSize + 2) + 38);

        // Trigger system (right side)
        const tx = 320, ty = 55;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Trigger System', tx, ty);

        const triggerLevels = [
            { name: 'L0 / L1 (Hardware)', rate: '40 MHz → 100 kHz', time: '~4 μs', color: '#3B82F6' },
            { name: 'HLT (Software)', rate: '100 kHz → 1 kHz', time: '~100 ms', color: '#22C55E' },
            { name: 'Offline / Storage', rate: '1 kHz → disk', time: 'seconds', color: '#8B5CF6' },
        ];
        triggerLevels.forEach((tl, i) => {
            const y = ty + 18 + i * 60;
            const w = 200 - i * 30;
            ctx.fillStyle = tl.color + '15'; ctx.beginPath(); ctx.roundRect(tx, y, w, 48, 8); ctx.fill();
            ctx.strokeStyle = tl.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(tx, y, w, 48, 8); ctx.stroke();
            ctx.fillStyle = tl.color; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(tl.name, tx + 10, y + 16);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(tl.rate, tx + 10, y + 32);
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(tl.time, tx + 10, y + 44);

            // Arrow
            if (i < triggerLevels.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = '14px Inter,sans-serif'; ctx.textAlign = 'center';
                ctx.fillText('↓', tx + w / 2, y + 56);
            }
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Triggers select interesting events from billions of collisions per second', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-indigo-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 6.10 – Detector Chapter Summary */
export const DetectorSummaryDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Detector System  -  Layer by Layer', W / 2, 24);

        const layers = [
            { label: 'Tracking', what: 'Charged tracks', measure: 'Momentum', color: '#3B82F6', w: 80 },
            { label: 'ECAL', what: 'e, γ showers', measure: 'EM Energy', color: '#22C55E', w: 70 },
            { label: 'HCAL', what: 'Hadron showers', measure: 'Had. Energy', color: '#F59E0B', w: 80 },
            { label: 'Muon', what: 'Muon tracks', measure: 'μ ID + p', color: '#EF4444', w: 70 },
        ];

        let x = 35;
        const y = 50, h = 150;
        layers.forEach((l, i) => {
            ctx.fillStyle = l.color + '15'; ctx.beginPath(); ctx.roundRect(x, y, l.w, h, 8); ctx.fill();
            ctx.strokeStyle = l.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, l.w, h, 8); ctx.stroke();
            ctx.save(); ctx.translate(x + l.w / 2, y + h / 2);
            ctx.fillStyle = l.color; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(l.label, 0, -25);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(l.what, 0, -5);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif';
            ctx.fillText(l.measure, 0, 18);
            ctx.restore();

            // Arrow between
            if (i < layers.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = '16px Inter,sans-serif'; ctx.textAlign = 'center';
                ctx.fillText('→', x + l.w + 12, y + h / 2 + 4);
            }
            x += l.w + 28;
        });

        // Particle entering
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(10, y + h / 2); ctx.lineTo(35, y + h / 2); ctx.stroke();
        ctx.fillStyle = '#EF4444';
        ctx.beginPath(); ctx.moveTo(35, y + h / 2); ctx.lineTo(29, y + h / 2 - 4); ctx.lineTo(29, y + h / 2 + 4); ctx.closePath(); ctx.fill();

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Each detector layer measures different particle properties  -  together they identify everything', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 7  -  The Standard Model
// ============================================

/** 7.1 – Three Generations of Matter */
export const ThreeGenerations = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Three Generations of Fundamental Fermions', W / 2, 24);

        const genX = [80, 230, 380];
        const genLabels = ['Generation I', 'Generation II', 'Generation III'];
        const genColors = ['#3B82F6', '#F59E0B', '#EF4444'];
        const genDesc = ['Stable matter', 'Short-lived', 'Very short-lived'];

        const particles = [
            // gen, type, name, mass, charge
            [0, 'quark', 'Up (u)', '2.2 MeV', '+2/3'],
            [0, 'quark', 'Down (d)', '4.7 MeV', '-1/3'],
            [0, 'lepton', 'Electron (e)', '0.511 MeV', '-1'],
            [0, 'lepton', 'νₑ', '<0.1 eV', '0'],
            [1, 'quark', 'Charm (c)', '1.27 GeV', '+2/3'],
            [1, 'quark', 'Strange (s)', '95 MeV', '-1/3'],
            [1, 'lepton', 'Muon (μ)', '106 MeV', '-1'],
            [1, 'lepton', 'νμ', '<0.1 eV', '0'],
            [2, 'quark', 'Top (t)', '173 GeV', '+2/3'],
            [2, 'quark', 'Bottom (b)', '4.18 GeV', '-1/3'],
            [2, 'lepton', 'Tau (τ)', '1.78 GeV', '-1'],
            [2, 'lepton', 'ντ', '<0.1 eV', '0'],
        ];

        // Generation columns
        genX.forEach((gx, gi) => {
            ctx.fillStyle = genColors[gi] + '10';
            ctx.beginPath(); ctx.roundRect(gx - 55, 40, 130, 240, 10); ctx.fill();
            ctx.strokeStyle = genColors[gi] + '40'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.roundRect(gx - 55, 40, 130, 240, 10); ctx.stroke();

            ctx.fillStyle = genColors[gi]; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(genLabels[gi], gx + 10, 58);
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(genDesc[gi], gx + 10, 72);
        });

        // Row labels
        ctx.fillStyle = '#475569'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('Quarks', 520, 115);
        ctx.fillText('Leptons', 520, 215);

        // Divider
        ctx.strokeStyle = '#E2E8F0'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(30, 165); ctx.lineTo(510, 165); ctx.stroke();
        ctx.setLineDash([]);

        particles.forEach(p => {
            const [gen, type, name, mass, charge] = p;
            const gx = genX[gen] + 10;
            const isQuark = type === 'quark';
            const row = isQuark ? (charge.includes('2/3') ? 0 : 1) : (charge === '-1' ? 0 : 1);
            const baseY = isQuark ? 85 : 180;
            const y = baseY + row * 38;

            ctx.fillStyle = genColors[gen];
            ctx.beginPath(); ctx.roundRect(gx - 55, y, 120, 32, 6); ctx.fill();
            ctx.globalAlpha = 0.15; ctx.fillStyle = 'white'; ctx.fill(); ctx.globalAlpha = 1;

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(name, gx - 48, y + 14);
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(mass, gx - 48, y + 26);
            ctx.fillStyle = genColors[gen]; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(charge, gx + 60, y + 14);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Only Generation I makes stable matter  -  heavier generations decay almost instantly', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.2 – Colour Charge and QCD */
export const ColorChargeQCD = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Colour Charge & QCD', W / 2, 24);

        // Three colour charges in a proton
        const px = 160, py = 150, pr = 70;
        ctx.fillStyle = '#F1F5F9'; ctx.beginPath(); ctx.arc(px, py, pr + 15, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(px, py, pr + 15, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#64748B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Proton', px, py + pr + 36);

        // Quarks at triangle vertices
        const qPositions = [
            { x: px, y: py - 42, color: '#EF4444', label: 'R', name: 'u' },
            { x: px - 38, y: py + 24, color: '#22C55E', label: 'G', name: 'u' },
            { x: px + 38, y: py + 24, color: '#3B82F6', label: 'B', name: 'd' },
        ];
        qPositions.forEach(q => {
            ctx.beginPath(); ctx.arc(q.x, q.y, 18, 0, Math.PI * 2); ctx.fillStyle = q.color; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(q.name, q.x, q.y + 5);
            ctx.fillStyle = q.color; ctx.font = 'bold 10px Inter,sans-serif';
            ctx.fillText(q.label, q.x + 22, q.y - 10);
        });

        // Gluon springs between quarks
        const drawSpring = (x1, y1, x2, y2) => {
            ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2;
            const dx = x2 - x1, dy = y2 - y1;
            const len = Math.sqrt(dx * dx + dy * dy);
            ctx.beginPath();
            for (let t = 0.15; t <= 0.85; t += 0.02) {
                const px2 = x1 + dx * t + Math.sin(t * 20) * (dy / len) * 5;
                const py2 = y1 + dy * t - Math.sin(t * 20) * (dx / len) * 5;
                t === 0.15 ? ctx.moveTo(px2, py2) : ctx.lineTo(px2, py2);
            }
            ctx.stroke();
        };
        qPositions.forEach((q, i) => {
            const next = qPositions[(i + 1) % 3];
            drawSpring(q.x, q.y, next.x, next.y);
        });
        ctx.fillStyle = '#F59E0B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('gluons', px, py + 5);

        // Right side: Rules
        const rx = 300, ry = 50;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('QCD Rules:', rx, ry);

        const rules = [
            { text: 'Quarks carry R, G, or B colour', icon: '🔴🟢🔵' },
            { text: 'Antiquarks carry anti-R, G, B', icon: '' },
            { text: 'Gluons carry colour + anti-colour', icon: '' },
            { text: 'Observable particles are "white"', icon: '' },
            { text: 'R + G + B = white (baryons)', icon: '' },
            { text: 'Colour + anti-colour = white (mesons)', icon: '' },
        ];
        rules.forEach((r, i) => {
            ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(`• ${r.text}`, rx, ry + 22 + i * 22);
        });

        // Asymptotic freedom note
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(rx - 5, ry + 165, 220, 50, 8); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(rx - 5, ry + 165, 220, 50, 8); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Asymptotic Freedom:', rx + 5, ry + 182);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Close → weak force', rx + 5, ry + 198);
        ctx.fillText('Far → STRONG force (confinement!)', rx + 5, ry + 212);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('QCD explains why quarks are permanently confined inside hadrons', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.3 – Hadrons: Baryons and Mesons */
export const HadronZoo = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Hadrons  -  Baryons vs Mesons', W / 2, 24);

        // Baryons (left)
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(20, 45, 250, 210, 12); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(20, 45, 250, 210, 12); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('BARYONS (3 quarks)', 145, 68);

        const baryons = [
            { name: 'Proton (p)', quarks: 'uud', charge: '+1', mass: '938 MeV' },
            { name: 'Neutron (n)', quarks: 'udd', charge: '0', mass: '940 MeV' },
            { name: 'Lambda (Λ)', quarks: 'uds', charge: '0', mass: '1116 MeV' },
            { name: 'Omega (Ω⁻)', quarks: 'sss', charge: '-1', mass: '1672 MeV' },
        ];
        baryons.forEach((b, i) => {
            const y = 82 + i * 40;
            ctx.fillStyle = '#3B82F6' + '15'; ctx.beginPath(); ctx.roundRect(30, y, 230, 34, 6); ctx.fill();
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(b.name, 38, y + 14);
            ctx.fillStyle = '#3B82F6'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(b.quarks, 38, y + 28);
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(`Q=${b.charge}  ${b.mass}`, 252, y + 22);
        });

        // Mesons (right)
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(290, 45, 250, 210, 12); ctx.fill();
        ctx.strokeStyle = '#FDE68A'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(290, 45, 250, 210, 12); ctx.stroke();
        ctx.fillStyle = '#D97706'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('MESONS (quark + antiquark)', 415, 68);

        const mesons = [
            { name: 'Pion (π⁺)', quarks: 'ud\u0304', charge: '+1', mass: '140 MeV' },
            { name: 'Kaon (K⁺)', quarks: 'us\u0304', charge: '+1', mass: '494 MeV' },
            { name: 'J/ψ', quarks: 'cc\u0304', charge: '0', mass: '3097 MeV' },
            { name: 'Upsilon (Υ)', quarks: 'bb\u0304', charge: '0', mass: '9460 MeV' },
        ];
        mesons.forEach((m, i) => {
            const y = 82 + i * 40;
            ctx.fillStyle = '#F59E0B' + '15'; ctx.beginPath(); ctx.roundRect(300, y, 230, 34, 6); ctx.fill();
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(m.name, 308, y + 14);
            ctx.fillStyle = '#D97706'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(m.quarks, 308, y + 28);
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(`Q=${m.charge}  ${m.mass}`, 522, y + 22);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('All hadrons are colour-neutral: baryons have RGB, mesons have colour + anti-colour', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.4 – The Higgs Mechanism */
export const HiggsMechanism = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Higgs Mechanism  -  How Particles Get Mass', W / 2, 24);

        // Higgs field (background grid)
        ctx.fillStyle = '#F5F3FF';
        ctx.beginPath(); ctx.roundRect(30, 45, 500, 190, 12); ctx.fill();

        // Field dots (uniform field)
        for (let r = 0; r < 8; r++) {
            for (let col = 0; col < 20; col++) {
                ctx.beginPath(); ctx.arc(55 + col * 25, 60 + r * 23, 2, 0, Math.PI * 2);
                ctx.fillStyle = '#C4B5FD'; ctx.fill();
            }
        }
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'italic 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Higgs Field (permeates all of space)', W / 2, 245);

        // Photon - no interaction (straight through)
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(50, 80); ctx.lineTo(230, 80); ctx.stroke();
        ctx.fillStyle = '#FCD34D';
        ctx.beginPath(); ctx.moveTo(230, 80); ctx.lineTo(224, 76); ctx.lineTo(224, 84); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#B45309'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Photon (m = 0)', 240, 75);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('No interaction → massless', 240, 90);

        // Electron - mild interaction (slightly wavy)
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let x = 50; x <= 220; x += 2) {
            const y = 140 + Math.sin((x - 50) * 0.08) * 8;
            x === 50 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Electron (m = 0.5 MeV)', 240, 135);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Weak coupling → small mass', 240, 150);

        // Top quark - strong interaction (very wavy, slow)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let x = 50; x <= 170; x += 2) {
            const y = 200 + Math.sin((x - 50) * 0.2) * 15;
            x === 50 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Top quark (m = 173 GeV)', 240, 195);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Strong coupling → very heavy', 240, 210);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Stronger coupling to Higgs field = more mass', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.5 – Higgs Discovery Timeline (case study) */
export const HiggsDiscoveryTimeline = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Road to the Higgs Boson', W / 2, 24);

        const events = [
            { year: '1964', label: 'Higgs mechanism\nproposed', color: '#3B82F6' },
            { year: '1983', label: 'W/Z found\n(validates EW)', color: '#22C55E' },
            { year: '2000', label: 'LEP hints at\n~115 GeV', color: '#F59E0B' },
            { year: '2012', label: 'Discovery!\n125.1 GeV', color: '#EF4444' },
            { year: '2013', label: 'Nobel Prize\nEnglert & Higgs', color: '#8B5CF6' },
        ];

        const axY = 110, axL = 50, axR = 510;
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(axL, axY); ctx.lineTo(axR, axY); ctx.stroke();

        const gap = (axR - axL) / (events.length - 1);
        events.forEach((ev, i) => {
            const x = axL + i * gap;
            ctx.beginPath(); ctx.arc(x, axY, 8, 0, Math.PI * 2);
            ctx.fillStyle = ev.color; ctx.fill();
            ctx.fillStyle = ev.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ev.year, x, axY - 18);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ev.label.split('\n').forEach((line, li) => {
                ctx.fillText(line, x, axY + 28 + li * 14);
            });
        });

        // Highlight the discovery
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
        const discX = axL + 3 * gap;
        ctx.beginPath(); ctx.arc(discX, axY, 16, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('July 4, 2012: "5-sigma" discovery by ATLAS and CMS at CERN', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('The last missing piece of the Standard Model  -  a 48-year search', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.6 – Antimatter */
export const AntimatterPairs = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 290;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Matter vs Antimatter', W / 2, 24);

        const pairs = [
            { matter: 'Electron (e⁻)', anti: 'Positron (e⁺)', mCol: '#3B82F6', aCol: '#EF4444' },
            { matter: 'Proton (p)', anti: 'Antiproton (p\u0304)', mCol: '#22C55E', aCol: '#F59E0B' },
            { matter: 'Neutron (n)', anti: 'Antineutron (n\u0304)', mCol: '#8B5CF6', aCol: '#EC4899' },
        ];

        const startY = 55;
        // Headers
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('MATTER', 140, startY);
        ctx.fillStyle = '#EF4444'; ctx.fillText('ANTIMATTER', 400, startY);
        ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Annihilation', 270, startY);

        pairs.forEach((p, i) => {
            const y = startY + 20 + i * 60;

            // Matter particle
            ctx.fillStyle = p.mCol + '15'; ctx.beginPath(); ctx.roundRect(40, y, 180, 45, 8); ctx.fill();
            ctx.strokeStyle = p.mCol; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(40, y, 180, 45, 8); ctx.stroke();
            ctx.beginPath(); ctx.arc(70, y + 22, 14, 0, Math.PI * 2); ctx.fillStyle = p.mCol; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.fillText('-', 70, y + 26);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(p.matter, 90, y + 27);

            // Annihilation arrows → γ
            ctx.fillStyle = '#FCD34D'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText('↔ γγ', 270, y + 27);

            // Antimatter particle
            ctx.fillStyle = p.aCol + '15'; ctx.beginPath(); ctx.roundRect(320, y, 180, 45, 8); ctx.fill();
            ctx.strokeStyle = p.aCol; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(320, y, 180, 45, 8); ctx.stroke();
            ctx.beginPath(); ctx.arc(350, y + 22, 14, 0, Math.PI * 2); ctx.fillStyle = p.aCol; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText('+', 350, y + 26);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(p.anti, 370, y + 27);
        });

        // Bottom note
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('E = 2mc² per annihilation', W / 2, startY + 210);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Every particle has an antimatter partner  -  same mass, opposite charges', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={290} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.7 – Neutrino Oscillations */
export const NeutrinoOscillations = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Neutrino Oscillations  -  Flavour Changing', W / 2, 24);

        const cy = 140, startX = 60, endX = 500;
        const colors = { e: '#3B82F6', mu: '#22C55E', tau: '#F59E0B' };

        // Three flavour waves (sine waves with different phases)
        const waves = [
            { label: 'νₑ', color: colors.e, phase: 0 },
            { label: 'νμ', color: colors.mu, phase: 2.1 },
            { label: 'ντ', color: colors.tau, phase: 4.2 },
        ];

        waves.forEach(w => {
            ctx.strokeStyle = w.color; ctx.lineWidth = 2.5;
            ctx.beginPath();
            for (let x = startX; x <= endX; x += 2) {
                const t = (x - startX) / (endX - startX);
                const amp = Math.sin(t * Math.PI * 3 + w.phase) * 0.5 + 0.5; // probability 0-1
                const y = cy - 60 + (1 - amp) * 120;
                x === startX ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            }
            ctx.stroke();
        });

        // Legend
        waves.forEach((w, i) => {
            const lx = 80 + i * 160;
            ctx.fillStyle = w.color; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(w.label, lx, 55);
            ctx.beginPath(); ctx.moveTo(lx - 20, 60); ctx.lineTo(lx + 20, 60);
            ctx.strokeStyle = w.color; ctx.lineWidth = 2.5; ctx.stroke();
        });

        // Y-axis label
        ctx.save(); ctx.translate(30, cy); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Probability', 0, 0); ctx.restore();

        // X-axis label
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Distance traveled', (startX + endX) / 2, cy + 80);

        // Top/bottom grid
        ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(startX, cy - 60); ctx.lineTo(endX, cy - 60); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(startX, cy + 60); ctx.lineTo(endX, cy + 60); ctx.stroke();
        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('1.0', startX - 6, cy - 56);
        ctx.fillText('0.0', startX - 6, cy + 64);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Neutrinos change flavour as they travel  -  proof they have mass!', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 7.11 – Standard Model Chapter Summary */
export const StandardModelSummaryChart = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Standard Model at a Glance', W / 2, 24);

        const categories = [
            { label: 'Quarks', count: '6', desc: '3 generations �- 2', color: '#3B82F6', x: 50 },
            { label: 'Leptons', count: '6', desc: '3 generations �- 2', color: '#22C55E', x: 155 },
            { label: 'Gauge\nBosons', count: '4', desc: 'γ, g, W±, Z⁰', color: '#F59E0B', x: 260 },
            { label: 'Scalar\nBoson', count: '1', desc: 'Higgs (H)', color: '#8B5CF6', x: 365 },
            { label: 'Anti-\nparticles', count: '12+', desc: 'Every fermion', color: '#EC4899', x: 470 },
        ];

        categories.forEach(cat => {
            const y = 50, h = 170, w = 85;
            ctx.fillStyle = cat.color + '10'; ctx.beginPath(); ctx.roundRect(cat.x - w / 2, y, w, h, 12); ctx.fill();
            ctx.strokeStyle = cat.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(cat.x - w / 2, y, w, h, 12); ctx.stroke();

            ctx.fillStyle = cat.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            const lines = cat.label.split('\n');
            lines.forEach((l, i) => ctx.fillText(l, cat.x, y + 22 + i * 15));

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 32px Inter,sans-serif';
            ctx.fillText(cat.count, cat.x, y + 90 + (lines.length > 1 ? 10 : 0));

            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
            const descLines = cat.desc.split('\n');
            descLines.forEach((l, i) => ctx.fillText(l, cat.x, y + h - 20 + i * 14));
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('17 fundamental particles + antiparticles  -  the most successful theory in physics', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-indigo-50 to-pink-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
